import type { FunctionComponent, SVGProps } from 'react';
import { TreeCommitteeIcon, TreeStateIcon, TreeAcIcon } from '../../assets/icons';
import type { AppDispatch } from '../../app/store';
import type { BoundaryDto } from '../organizations/organizationsApi';
import { territoryApi, type VillageDto } from './territoryApi';

/**
 * The dynamic constituency hierarchy used by Create-Org step 2 (and the engagement
 * EC-Level picker). Every level below the static root is fetched from the territory API
 * when its parent expands, passing the parent's code down:
 *
 *   root → State → Parliament → Assembly → (Urban | Rural)
 *     Urban → ULB → Ward → Village → Booth
 *     Rural → Gram Panchayat → Village → Booth
 */

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;
export type Branch = 'urban' | 'rural';

export type NodeKind =
  | 'root'
  | 'state'
  | 'parliament'
  | 'assembly'
  | 'branch'
  | 'ulb'
  | 'ward'
  | 'gram'
  | 'village'
  | 'booth';

export interface TreeNode {
  id: string;
  kind: NodeKind;
  label: string;
  /** Code passed to fetch THIS node's children (state id, pc id, ac_code, block_ulb_code, …). */
  code?: string;
  /** Real area code for the EC scope API (state_code, pc_code, ac_code, ps_code, …). */
  areaCode?: string;
  /** Assembly ac_code — carried onto the Rural branch so it can query gram panchayats. */
  acCode?: string;
  branch?: Branch;
  acId?: string;
  acLabel?: string;
  icon?: IconType;
  level?: string;
  sub?: string;
  groupLabel?: string;
  /** EC location level id, when seeded from a boundary — used for the owner lookup. */
  ecLevelId?: number;
}

/** EC location level (name + rank) each selectable node kind maps to, for the scope API. */
export const EC_LEVEL: Partial<Record<NodeKind, { name: string; rank: number }>> = {
  state: { name: 'State', rank: 1 },
  parliament: { name: 'MP', rank: 2 },
  assembly: { name: 'Assembly', rank: 4 },
  ulb: { name: 'Block', rank: 5 },
  gram: { name: 'GramPanchayat', rank: 6 },
  village: { name: 'Village', rank: 7 },
  ward: { name: 'Ward', rank: 8 },
  booth: { name: 'Booth', rank: 9 },
};

export const ROOT: TreeNode = {
  id: 'root',
  kind: 'root',
  label: 'National Central Committee',
  icon: TreeCommitteeIcon,
  level: 'LEVEL 1',
};

export const canHaveChildren = (node: TreeNode) => node.kind !== 'booth';

/** Build a Village tree node (shared by the urban & rural paths). */
export function villageNode(id: string, v: VillageDto, branch: Branch, parent: TreeNode): TreeNode {
  const name = v.name_en || `Village ${v.village_pk}`;
  return {
    id,
    kind: 'village',
    label: v.census_code ? `${name} · ${v.census_code}` : name,
    code: v.village_result_code || '',
    areaCode: v.village_result_code || '',
    branch,
    acId: parent.acId,
    acLabel: parent.acLabel,
    acCode: parent.acCode,
    groupLabel: 'Booths',
    sub: v.total_electors ? `${v.total_electors} electors` : undefined,
  };
}

/** Fetch a node's children from the appropriate territory endpoint. */
export async function fetchConstituencyChildren(
  dispatch: AppDispatch,
  node: TreeNode,
  search?: string,
): Promise<TreeNode[]> {
  const ep = territoryApi.endpoints;
  const cid = (suffix: string) => `${node.id}/${suffix}`;
  const one = <T,>(p: { unwrap: () => Promise<T> }) => p.unwrap();

  switch (node.kind) {
    case 'root': {
      const states = await one(
        dispatch(ep.getStates.initiate(search ? { search } : undefined, { subscribe: false })),
      );
      return states.map((s, i) => ({
        id: cid(`state-${s.id ?? i}`),
        kind: 'state',
        label: s.name || `State ${s.id}`,
        code: String(s.id),
        areaCode: s.state_code || s.state_cd || '',
        icon: TreeStateIcon,
        level: 'LEVEL 2',
        groupLabel: 'Parliament consituency',
      }));
    }
    case 'state': {
      const pcs = await one(
        dispatch(ep.getParliaments.initiate({ state: node.code! }, { subscribe: false })),
      );
      return pcs.map((p, i) => ({
        id: cid(`pc-${p.id ?? i}`),
        kind: 'parliament',
        label: p.name || `PC ${p.id}`,
        code: String(p.id),
        areaCode: p.pc_code || '',
        icon: TreeAcIcon,
        groupLabel: 'Assembly consituency',
      }));
    }
    case 'parliament': {
      const acs = await one(
        dispatch(ep.getAssemblies.initiate({ pc: node.code! }, { subscribe: false })),
      );
      return acs.map((a, i) => {
        const id = cid(`ac-${a.id ?? i}`);
        const label = a.name || `AC ${a.id}`;
        return {
          id,
          kind: 'assembly',
          label,
          code: a.ac_code || '',
          areaCode: a.ac_code || '',
          acCode: a.ac_code || '',
          icon: TreeAcIcon,
          groupLabel: 'Settlement type',
          sub: a.ac_code ? `AC code ${a.ac_code}` : undefined,
          acId: id,
          acLabel: label,
        } as TreeNode;
      });
    }
    case 'assembly': {
      // Static settlement branches (Urban / Rural).
      return [
        {
          id: cid('urban'),
          kind: 'branch',
          branch: 'urban',
          label: 'Urban',
          groupLabel: 'Urban local body',
          acId: node.acId,
          acLabel: node.acLabel,
          acCode: node.acCode ?? node.code,
        },
        {
          id: cid('rural'),
          kind: 'branch',
          branch: 'rural',
          label: 'Rural',
          groupLabel: 'Gram Panchayat',
          acId: node.acId,
          acLabel: node.acLabel,
          acCode: node.acCode ?? node.code,
        },
      ];
    }
    case 'branch': {
      if (node.branch === 'urban') {
        const ulbs = await one(dispatch(ep.getUlbs.initiate(undefined, { subscribe: false })));
        return ulbs.map((u, i) => ({
          id: cid(`ulb-${u.block_ulb_code ?? u.block_pk ?? i}`),
          kind: 'ulb',
          label: u.name_en || `ULB ${u.block_pk}`,
          code: u.block_ulb_code || '',
          areaCode: u.block_ulb_code || '',
          branch: 'urban',
          groupLabel: 'Ward',
          acId: node.acId,
          acLabel: node.acLabel,
          acCode: node.acCode,
        }));
      }
      const gps = await one(
        dispatch(ep.getGramPanchayats.initiate({ acCode: node.acCode || '' }, { subscribe: false })),
      );
      return gps.map((g, i) => ({
        id: cid(`gram-${g.gram_panchayat_lgd_code ?? g.gram_panchayat_pk ?? i}`),
        kind: 'gram',
        label: g.name_en || `GP ${g.gram_panchayat_pk}`,
        code: g.gram_panchayat_lgd_code || '',
        areaCode: g.gram_panchayat_lgd_code || '',
        branch: 'rural',
        groupLabel: 'Village',
        acId: node.acId,
        acLabel: node.acLabel,
        acCode: node.acCode,
      }));
    }
    case 'ulb': {
      const wards = await one(
        dispatch(ep.getWards.initiate({ ulbCode: node.code! }, { subscribe: false })),
      );
      return wards.map((w, i) => ({
        id: cid(`ward-${w.ward_code ?? w.ward_pk ?? i}`),
        kind: 'ward',
        label: w.ward_name_en || `Ward ${w.ward_pk}`,
        code: w.ward_code || '',
        areaCode: w.ward_code || '',
        branch: 'urban',
        groupLabel: 'Village',
        acId: node.acId,
        acLabel: node.acLabel,
        acCode: node.acCode,
      }));
    }
    case 'ward': {
      const vs = await one(
        dispatch(ep.getUrbanVillages.initiate({ wardCode: node.code! }, { subscribe: false })),
      );
      return vs.map((v, i) =>
        villageNode(cid(`village-${v.village_result_code ?? v.village_pk ?? i}`), v, 'urban', node),
      );
    }
    case 'gram': {
      const vs = await one(
        dispatch(ep.getRuralVillages.initiate({ gpCode: node.code! }, { subscribe: false })),
      );
      return vs.map((v, i) =>
        villageNode(cid(`village-${v.village_result_code ?? v.village_pk ?? i}`), v, 'rural', node),
      );
    }
    case 'village': {
      const booths = await one(
        dispatch(ep.getBooths.initiate({ villageResultCode: node.code! }, { subscribe: false })),
      );
      return booths.map((b, i) => ({
        id: cid(`booth-${b.ps_code ?? i}`),
        kind: 'booth',
        label: b.booth_name
          ? `${b.booth_name}${b.ps_code ? ` · ${b.ps_code}` : ''}`
          : b.ps_code
            ? `Booth ${b.ps_code}`
            : `Booth ${i + 1}`,
        areaCode: b.ps_code || '',
        branch: node.branch,
        acId: node.acId,
        acLabel: node.acLabel,
        acCode: node.acCode,
      }));
    }
    default:
      return [];
  }
}

const normLevel = (s?: string | null) => (s || '').toLowerCase().replace(/[\s_-]+/g, '');
const KIND_BY_LEVEL: Record<string, NodeKind> = {
  state: 'state',
  parliament: 'parliament',
  mp: 'parliament',
  assembly: 'assembly',
  mla: 'assembly',
  block: 'ulb',
  ulb: 'ulb',
  urbanlocalbody: 'ulb',
  grampanchayat: 'gram',
  gp: 'gram',
  village: 'village',
  ward: 'ward',
  booth: 'booth',
  ps: 'booth',
};

/** Map a boundary's `level_name` to a tree node kind. */
export function kindFromLevelName(level?: string | null): NodeKind | null {
  return KIND_BY_LEVEL[normLevel(level)] ?? null;
}

/**
 * Turn one org boundary (GET /organisation/boundary/) into a lazy tree seed node —
 * resolving its territory code so `fetchConstituencyChildren` can drill below it
 * (State/Parliament need an internal id; deeper levels use the area_code directly).
 */
export async function resolveBoundarySeed(
  dispatch: AppDispatch,
  b: BoundaryDto,
): Promise<TreeNode | null> {
  const kind = kindFromLevelName(b.level_name);
  if (!kind) return null;
  const areaCode = b.area_code || '';
  const name = b.area_name || areaCode;
  const ep = territoryApi.endpoints;
  const run = <T,>(p: { unwrap: () => Promise<T> }) => p.unwrap();

  const node: TreeNode = {
    id: `bnd-${b.id ?? areaCode}`,
    kind,
    label: areaCode ? `${name} · ${areaCode}` : name,
    areaCode,
    code: areaCode,
    acCode: b.context?.assembly?.code ?? (kind === 'assembly' ? areaCode : undefined),
    ecLevelId: b.ec_location_level_id ?? undefined,
    icon: kind === 'state' ? TreeStateIcon : kind === 'parliament' ? TreeAcIcon : undefined,
    groupLabel:
      kind === 'state'
        ? 'Parliament consituency'
        : kind === 'parliament'
          ? 'Assembly consituency'
          : kind === 'assembly'
            ? 'Settlement type'
            : undefined,
  };

  try {
    if (kind === 'state') {
      const states = await run(dispatch(ep.getStates.initiate(undefined, { subscribe: false })));
      const s = states.find(
        (x) =>
          x.state_code === areaCode || x.state_cd === areaCode || x.name === b.context?.state?.name,
      );
      if (s) node.code = String(s.id);
    } else if (kind === 'parliament') {
      const pcs = await run(
        dispatch(ep.getParliamentByPcCode.initiate({ pcCode: areaCode }, { subscribe: false })),
      );
      if (pcs?.[0]) node.code = String(pcs[0].id);
    } else if (kind === 'assembly') {
      // ac_code is the code; the Rural branch reads acCode off the node.
      node.acId = node.id;
      node.acLabel = name;
    } else if (kind === 'gram') {
      node.branch = 'rural';
    } else if (kind === 'ulb' || kind === 'ward') {
      node.branch = 'urban';
    }
  } catch {
    /* keep area_code as code — children may fail to load but the node still shows */
  }
  return node;
}
